import { queryOptions } from '@tanstack/react-query'

import { fetchJson, path } from '@/api/client'
import { keys } from '@/api/keys'
import type { IndexExclusions } from '@/schema'

/**
 * `/api/index/*` (§5.4): runs, status, exclusions, overrides import. The
 * `POST /api/index/runs` starter lives in `api/jobs.ts` next to the other
 * job starters.
 */

export type IndexRunStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled'

export interface IndexRunStats {
  lists?: number
  gets?: number
  heads?: number
  objects?: number
  packages?: number
  assets?: number
  clips?: number
  broken_clips?: number
  sheet_bytes_total?: number
  clip_frame_count_histogram?: Record<string, number>
  mp4_bytes_max?: number
  enqueued?: Record<string, number>
  [k: string]: unknown
}

export interface IndexRun {
  id: number
  mode: 'full' | 'incremental'
  status: IndexRunStatus
  started_at: string | null
  finished_at: string | null
  stats: IndexRunStats
  warnings: string[]
  job_id?: number
}

export interface IndexStatus {
  last_run: IndexRun | null
  running_job_id: number | null
  counts: {
    background: number
    live_background: number
    character: number
    character_skins: number
    object: number
    audio: number
    clips: number
    broken_clips: number
  }
  seeded: number
}

export function listIndexRuns(limit = 20, signal?: AbortSignal): Promise<{ items: IndexRun[] }> {
  return fetchJson('/api/index/runs', { query: { limit }, signal })
}

export function getIndexRun(id: number, signal?: AbortSignal): Promise<IndexRun> {
  return fetchJson(path`/api/index/runs/${id}`, { signal })
}

export function getIndexStatus(signal?: AbortSignal): Promise<IndexStatus> {
  return fetchJson('/api/index/status', { signal })
}

export function getExclusions(signal?: AbortSignal): Promise<IndexExclusions> {
  return fetchJson('/api/index/exclusions', { signal })
}

export function putExclusions(body: IndexExclusions): Promise<IndexExclusions> {
  return fetchJson('/api/index/exclusions', { method: 'PUT', body })
}

/** `manifests/asset_overrides.json` → `asset_overrides` table, as a job. */
export function importOverrides(): Promise<{ job_id: number }> {
  return fetchJson('/api/index/import-overrides', { method: 'POST' })
}

/* ------------------------------------------------------------- queries --- */

export const indexStatusQuery = queryOptions({
  queryKey: keys.index.status(),
  queryFn: ({ signal }) => getIndexStatus(signal),
  staleTime: 10_000,
  retry: 1,
})

export const indexRunsQuery = (limit = 20) =>
  queryOptions({
    queryKey: keys.index.runs(),
    queryFn: ({ signal }) => listIndexRuns(limit, signal),
    staleTime: 10_000,
    retry: 1,
  })

export const indexRunQuery = (id: number) =>
  queryOptions({
    queryKey: keys.index.run(id),
    queryFn: ({ signal }) => getIndexRun(id, signal),
    staleTime: 5_000,
  })

export const exclusionsQuery = queryOptions({
  queryKey: keys.index.exclusions(),
  queryFn: ({ signal }) => getExclusions(signal),
  staleTime: 60_000,
})
